import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Alert, Box } from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { useDocumentContext } from '../../document-context';
import { useEditDocument } from '../edit-document-context';

import {
  markdownMessageEditValuesToBlockData,
  messageBlockDataToEditValues,
  normalizeMessageBlockData,
  parseMarkdownMessageConfig,
  serializeMarkdownMessageBlock,
  serializeMarkdownMessageInnerConfig,
  stringifyMarkdownMessageConfig,
  type MarkdownMessageEditValues,
  type MessageBlockData,
  NP_MESSAGE_TAG,
} from './markdown-message';
import { NpMessageBlockPanel } from './np-message-block-panel';

interface MarkdownMessageNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownMessageNodeAttrs {
  data: MessageBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeMessageAttrs(attrs: unknown): MarkdownMessageNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};

  return {
    data: normalizeMessageBlockData(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownMessageNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownMessageNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeMessageAttrs(node.attrs);
  const {
    documentId,
    isNewDocument,
    isMarkdownDocument,
    getSerializedDocument,
  } = useDocumentContext();
  const { getValue } = useEditDocument();

  const handleEdit = () => {
    context.onRequestMessageEdit?.({
      mode: 'edit',
      initialValues: messageBlockDataToEditValues(attrs.data),
      onCommit: async (next: MarkdownMessageEditValues) => {
        const normalized = markdownMessageEditValuesToBlockData(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyMarkdownMessageConfig(normalized),
          parseError: null,
        });
      },
    });
  };

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-message-node markdown-message-node--selected'
          : 'markdown-message-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <Box
        borderWidth="1px"
        borderColor="border.muted"
        borderRadius="md"
        p={3}
        minH="20rem"
        maxH="24rem"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        bg="bg.subtle"
      >
        {attrs.parseError ? (
          <Alert.Root status="warning" borderRadius="md" mb={2}>
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Invalid message config</Alert.Title>
              <Alert.Description>{attrs.parseError}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
        ) : null}

        <NpMessageBlockPanel
          config={attrs.data}
          documentId={documentId}
          isNewDocument={isNewDocument}
          isMarkdownDocument={isMarkdownDocument}
          getSerializedDocument={getSerializedDocument}
          getEditorValue={getValue}
          canEdit={Boolean(context.onRequestMessageEdit)}
          onRequestEdit={handleEdit}
        />
      </Box>
    </NodeViewWrapper>
  );
}

export const MarkdownMessageNode = Node.create<MarkdownMessageNodeOptions>({
  name: 'npMessage',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeMessageBlockData({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_MESSAGE_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownMessageConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeMessageAttrs(HTMLAttributes);
    return [NP_MESSAGE_TAG, serializeMarkdownMessageInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeMessageAttrs(node?.attrs);
    return serializeMarkdownMessageBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownMessageNodeView);
  },
});
